<template>
  <div class="w-full bg-white overflow-x-hidden">
    <!-- Hero Section -->
    <section class="min-h-[40vh] relative bg-gradient-to-r from-[#0D0971] to-[#1a1a8e]">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 py-12">
        <h1 class="font-bold text-3xl text-white mb-4">
          {{ categoryData?.title || 'Category Details' }}
        </h1>
        <div class="w-24 h-1 bg-white/50 mb-6"></div>
        <button
          @click="goBack"
          class="mt-4 px-6 py-2 bg-white text-[#0D0971] font-semibold text-sm rounded-lg hover:bg-gray-100 transition-colors duration-300 flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>

    <!-- Main Content -->
    <section v-if="categoryData" class="py-8">
      <div class="px-6">
        <!-- Description -->
        <div v-if="categoryData.description" class="mb-8">
          <p class="text-base text-[#444] leading-relaxed" v-html="categoryData.description"></p>
        </div>

        <!-- Applications -->
        <div v-if="categoryData?.applications" class="mb-8">
          <h2 class="text-xl font-bold text-[#0D0971] mb-4 flex items-center gap-2">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            {{ t('categoryDetail.applications') }}
          </h2>
          <div v-if="Array.isArray(categoryData.applications)" class="bg-white rounded-lg shadow-md p-5 border border-gray-100">
            <ul class="space-y-3">
              <li v-for="(app, index) in categoryData.applications"
                  :key="index"
                  class="flex items-start gap-2 text-sm text-[#444]">
                <span class="text-[#0D0971] font-bold mt-0.5">•</span>
                <span>{{ app }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Certifications -->
        <div v-if="categoryData?.certifications" class="mb-8">
          <h2 class="text-xl font-bold text-[#0D0971] mb-4 flex items-center gap-2">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            {{ t('categoryDetail.certifications') }}
          </h2>
          <div class="bg-white rounded-lg shadow-md p-5 border border-gray-100">
            <ul class="space-y-3">
              <li v-for="(cert, index) in categoryData.certifications"
                  :key="index"
                  class="flex items-start gap-2 text-sm text-[#444]">
                <svg class="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                </svg>
                <span>{{ cert }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Sections (Partner Strengths) -->
        <div v-if="categoryData.sections && categoryData.sections.length > 0">
          <div v-for="(section, sectionIndex) in categoryData.sections" :key="sectionIndex" class="mb-8 mt-8">
            <!-- Section Title -->
            <h2 class="text-2xl font-bold text-[#0D0971] mb-6 flex items-center gap-2">
              <span class="text-xl">{{ sectionIndex + 1 }} |</span>
              {{ section.title }}
            </h2>

            <!-- Section Items (for items array) -->
            <div v-if="section.items && section.items.length > 0" class="space-y-4">
              <div v-for="(item, index) in section.items" :key="index"
                   class="bg-gradient-to-br from-[#f3F8FF] to-[#e8f0fe] rounded-lg p-5 border-l-4 border-[#0D0971]">
                <!-- Item Name -->
                <h3 class="text-base font-bold text-[#0D0971]" :class="{ 'mb-2': item.description }">{{ item.name }}</h3>

                <!-- Item Description (only if exists) -->
                <p v-if="item.description" class="text-sm text-[#444] leading-relaxed">
                  {{ item.description }}
                </p>
              </div>
            </div>

            <!-- Products (for products array with features and photos table) -->
            <div v-if="section.products && section.products.length > 0">
              <!-- Section Description (if exists) -->
              <p v-if="section.description" class="text-sm text-[#444] leading-relaxed mb-4">
                {{ section.description }}
              </p>

              <div v-for="(product, index) in section.products" :key="index" class="mb-6">
                <!-- Product Name -->
                <h3 class="text-lg font-bold text-black mb-3">{{ product.name }}</h3>

                <!-- Product Description (standalone, if no features/photos) -->
                <p v-if="product.description && (!product.features || product.features.length === 0) && (!product.photos || product.photos.length === 0)"
                   class="text-sm text-[#444] leading-relaxed mb-3">
                  {{ product.description }}
                </p>

                <!-- Features and Photos Table (only if features or photos exist) -->
                <div v-if="(product.features && product.features.length > 0) || (product.photos && product.photos.length > 0)"
                     class="border border-gray-300 rounded-lg overflow-hidden">
                  <!-- Table Header -->
                  <div class="grid grid-cols-2 bg-[#0D0971]">
                    <div class="px-3 py-2 text-white text-sm font-bold text-center border-r border-white">
                      {{ t('categoryDetail.tableHeaders.features') }}
                    </div>
                    <div class="px-3 py-2 text-white text-sm font-bold text-center">
                      {{ t('categoryDetail.tableHeaders.photos') }}
                    </div>
                  </div>

                  <!-- Table Body -->
                  <div class="grid grid-cols-2">
                    <!-- Features Column -->
                    <div class="px-3 py-4 bg-[#f5f5f5] border-r border-gray-300">
                      <ul v-if="product.features && product.features.length > 0" class="space-y-2">
                        <li v-for="(feature, fIndex) in product.features"
                            :key="fIndex"
                            class="flex items-start gap-2 text-xs text-black leading-relaxed">
                          <span class="text-[#0D0971] font-bold mt-0.5">•</span>
                          <span v-html="feature"></span>
                        </li>
                      </ul>
                      <p v-else class="text-xs text-[#999] text-center">{{ t('categoryDetail.notAvailable') }}</p>
                    </div>

                    <!-- Photos Column -->
                    <div class="px-3 py-4 bg-white">
                      <div v-if="product.photos && product.photos.length > 0"
                           :class="product.photos.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 gap-2'">
                        <div v-for="(photo, pIndex) in product.photos"
                             :key="pIndex"
                             class="flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden aspect-square">
                          <img :src="photo"
                               :alt="`${product.name} - Photo ${pIndex + 1}`"
                               class="w-full h-full object-contain"
                               @error="handleImageError">
                        </div>
                      </div>
                      <p v-else class="text-xs text-[#999] text-center">{{ t('categoryDetail.notAvailable') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Solutions - Custom Fabrication -->
        <div v-if="categoryData?.solutionsCustomFabrication" class="mt-8">
          <h2 class="text-2xl font-bold text-[#0D0971] mb-4 flex items-center gap-2">
            <span class="text-xl">4 |</span>
            {{ categoryData.solutionsCustomFabrication.title }}
          </h2>

          <!-- Description (if exists) -->
          <p v-if="categoryData.solutionsCustomFabrication.description" class="text-sm text-[#444] mb-4">
            {{ categoryData.solutionsCustomFabrication.description }}
          </p>

          <!-- Solutions Table -->
          <div class="border border-gray-300 rounded-lg overflow-hidden">
            <!-- Table Header -->
            <div class="grid grid-cols-2 bg-[#0D0971] text-white">
              <div class="text-sm font-bold py-2 px-3 text-center border-r border-gray-300">
                {{ t('categoryDetail.tableHeaders.solution') }}
              </div>
              <div class="text-sm font-bold py-2 px-3 text-center">
                {{ t('categoryDetail.tableHeaders.introductoryText') }}
              </div>
            </div>

            <!-- Table Rows -->
            <div v-for="(item, index) in categoryData.solutionsCustomFabrication.solutions"
                 :key="index"
                 class="grid grid-cols-2"
                 :class="{ 'bg-gray-50': index % 2 === 1 }">
              <!-- Solution Column -->
              <div class="py-3 px-3 border-r border-gray-300 border-t border-gray-300 flex items-center">
                <span class="text-xs text-[#333] font-medium">{{ item.solution }}</span>
              </div>

              <!-- Introduction Column -->
              <div class="py-3 px-3 border-t border-gray-300">
                <ul class="space-y-2">
                  <li v-for="(intro, iIndex) in item.introduction"
                      :key="iIndex"
                      class="flex items-start gap-2 text-xs text-[#444]">
                    <span class="text-[#0D0971] font-bold mt-0.5">•</span>
                    <span>{{ intro }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Category Not Found -->
    <section v-if="!categoryData" class="py-16">
      <div class="px-6 text-center">
        <h2 class="text-2xl font-bold text-[#0D0971] mb-4">{{ t('categoryDetail.categoryNotFound') }}</h2>
        <p class="text-base text-[#666] mb-6">
          {{ t('categoryDetail.categoryNotFoundMessage') }}
        </p>
        <button
          @click="goBack"
          class="px-6 py-3 bg-[#0D0971] text-white font-semibold text-base rounded-lg hover:bg-[#1a1a8e] transition-colors duration-300">
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';

const router = useRouter();
const { locale, messages, t } = useI18n();

// Get Silos and Tanks category data from i18n
const categoryData = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const silosCategory = categories.find((cat: any) => cat.id === 'silos-tanks');
  return silosCategory?.detailContent || null;
});

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});

const goBack = () => {
  router.push('/core-offers/source-product');
  pushToDataLayer('click', {
    category: 'MobileCategorySilosAndTanksDetail',
    label: 'Back to Source Product',
  });
};

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("MobileCategorySilosAndTanksDetail");
  pushToDataLayer('click', {
    category: 'MobileCategorySilosAndTanksDetail',
    label: 'Contact Us',
  });
};

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.src = '/images/placeholder.jpg';
};
</script>

